// A booking's stages before the function: the vendor confirms each in order and uploads its
// vouchers and photos; the bride/groom family, their matchmaker and account manager follow it.
import React, { useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import {
  Box, Stack, Typography, Chip, Button, LinearProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Alert, IconButton, CircularProgress, Link, Collapse,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import PendingIcon from '@mui/icons-material/Pending';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { SUBMIT_BOOKING_STAGE_MUTATION } from '../graphql/queries';
import { mediaUrl, uploadBookingFile } from '../utils/media';
import PhotoStrip from './PhotoStrip';

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '');

function VoucherLinks({ files }) {
  const vouchers = files.filter(f => f.kind === 'voucher');
  if (!vouchers.length) return null;
  return (
    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
      {vouchers.map(f => (
        <Chip
          key={f.id} size="small" variant="outlined" icon={<ReceiptLongIcon />} clickable
          component={Link} href={mediaUrl(f.url)} target="_blank" rel="noopener noreferrer" label={f.name}
        />
      ))}
    </Stack>
  );
}

function StageRow({ st, canConfirm, onConfirm }) {
  const photos = st.files.filter(f => f.kind === 'photo').map(f => ({ url: f.url, caption: `${st.label} — ${f.name}` }));
  const icon = st.status === 'done' ? <CheckCircleIcon color="success" fontSize="small" />
    : st.status === 'next' ? <PendingIcon color="warning" fontSize="small" />
      : <RadioButtonUncheckedIcon color="disabled" fontSize="small" />;
  return (
    <Stack direction="row" spacing={1.25} alignItems="flex-start">
      <Box pt={0.25}>{icon}</Box>
      <Box flex={1} minWidth={0}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="body2" fontWeight={600}>{st.label}</Typography>
          {st.status === 'done' && st.confirmedAt && <Typography variant="caption" color="text.secondary">{fmt(st.confirmedAt)}</Typography>}
          {st.late && <Chip size="small" color="warning" variant="outlined" label="On the function day" sx={{ height: 18, fontSize: 10 }} />}
          {st.status === 'next' && <Chip size="small" color="warning" label="Next" sx={{ height: 18, fontSize: 10 }} />}
        </Stack>
        {st.status !== 'done' && <Typography variant="caption" color="text.secondary" display="block">{st.description}</Typography>}
        {st.note && <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>{st.note}</Typography>}
        {st.files.length > 0 && (
          <Stack spacing={0.75} mt={0.5}>
            <VoucherLinks files={st.files} />
            <PhotoStrip photos={photos} size={48} max={6} />
          </Stack>
        )}
        {canConfirm && st.status === 'next' && (
          <Button size="small" variant="contained" sx={{ mt: 0.75 }} onClick={() => onConfirm(st)}>Confirm & upload</Button>
        )}
      </Box>
    </Stack>
  );
}

// Upload list for one kind of file (vouchers / photos)
function FilePicker({ label, accept, kind, selectionId, files, onAdd, onRemove, icon }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const fileInput = useRef(null);
  const add = async (list) => {
    setBusy(true); setError('');
    for (const file of [...list]) {
      try { onAdd(await uploadBookingFile(selectionId, kind, file)); } catch (e) { setError(e.message); }
    }
    setBusy(false);
  };
  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <input ref={fileInput} type="file" accept={accept} multiple hidden onChange={e => { add(e.target.files); e.target.value = ''; }} />
        <Button size="small" variant="outlined" disabled={busy} onClick={() => fileInput.current?.click()} startIcon={busy ? <CircularProgress size={14} /> : icon}>
          {busy ? 'Uploading…' : label}
        </Button>
        {files.map(f => (
          <Chip
            key={f.id} size="small" label={f.name} onDelete={() => onRemove(f.id)} deleteIcon={<CloseIcon />}
            {...(f.mimeType.startsWith('image/') && {
              avatar: <Box component="span" sx={{ width: 20, height: 20, borderRadius: '50%', background: `center / cover url("${mediaUrl(f.url)}")` }} />,
            })}
          />
        ))}
      </Stack>
      {error && <Typography variant="caption" color="error">{error}</Typography>}
    </Box>
  );
}

function ConfirmStageDialog({ booking, stage, onClose, onDone }) {
  const [note, setNote] = useState('');
  const [files, setFiles] = useState([]);
  const [error, setError] = useState('');
  const [submit, { loading }] = useMutation(SUBMIT_BOOKING_STAGE_MUTATION, {
    onCompleted: () => { onDone(`"${stage.label}" confirmed — the family and their account manager are notified.`); },
    onError: (e) => setError(e.message),
  });
  const of = (kind) => files.filter(f => f.kind === kind);
  const ready = stage.requires === 'voucher' ? of('voucher').length > 0
    : stage.requires === 'photo' ? of('photo').length > 0 : note.trim().length > 0;
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{stage.label}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" mb={2}>
          {stage.description} For {booking.familyName}'s {booking.offeringTitle} on {fmt(`${booking.requiredDate}T00:00:00`)}.
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Stack spacing={2}>
          <FilePicker
            label={stage.requires === 'voucher' ? 'Upload voucher / receipt *' : 'Upload voucher / invoice'}
            accept="application/pdf,image/jpeg,image/png,image/webp" kind="voucher" icon={<ReceiptLongIcon />}
            selectionId={booking.id} files={of('voucher')}
            onAdd={(f) => setFiles(l => [...l, f])} onRemove={(id) => setFiles(l => l.filter(x => x.id !== id))}
          />
          <FilePicker
            label={stage.requires === 'photo' ? 'Upload photos *' : 'Upload photos'}
            accept="image/jpeg,image/png,image/webp" kind="photo" icon={<AddAPhotoIcon />}
            selectionId={booking.id} files={of('photo')}
            onAdd={(f) => setFiles(l => [...l, f])} onRemove={(id) => setFiles(l => l.filter(x => x.id !== id))}
          />
          <TextField
            label={stage.requires === 'note' ? 'Confirmation note *' : 'Note (optional)'} multiline minRows={2} fullWidth
            value={note} onChange={e => setNote(e.target.value)}
            placeholder={stage.key === 'final' ? 'e.g. Team of 12 arrives 5 am; menu and quantities as agreed' : stage.key === 'advance' ? 'e.g. ₹50,000 advance received by bank transfer' : ''}
          />
          <Typography variant="caption" color="text.secondary">
            The family, their secondary contact, their matchmaker and account manager will see this and the files.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!ready || loading}
          onClick={() => submit({ variables: { selectionId: booking.id, stage: stage.key, note: note.trim() || null, fileIds: files.map(f => f.id) } })}>
          {loading ? 'Confirming…' : 'Confirm stage'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// forVendor: the vendor's own booking (can confirm the next stage); otherwise read-only
export default function BookingProgress({ booking, forVendor = false, onMessage, defaultOpen = false }) {
  const p = booking.progress;
  const [open, setOpen] = useState(defaultOpen || (forVendor && Boolean(p?.nextStage)));
  const [confirming, setConfirming] = useState(null);
  if (!p || !['confirmed', 'completed'].includes(booking.status)) return null;
  const next = p.stages.find(s => s.status === 'next');
  return (
    <Box mt={1.5}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ cursor: 'pointer' }} onClick={() => setOpen(o => !o)}>
        <Typography variant="caption" fontWeight={700} color="text.secondary">
          BEFORE THE FUNCTION · {p.done}/{p.total} STAGES
        </Typography>
        <LinearProgress variant="determinate" value={(p.done / p.total) * 100} color={p.allDone ? 'success' : 'primary'} sx={{ flex: 1, height: 6, borderRadius: 3 }} />
        {next && !open && <Chip size="small" color="warning" variant="outlined" label={`Next: ${next.label}`} sx={{ height: 20, fontSize: 11 }} />}
        {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
      </Stack>
      <Collapse in={open}>
        <Stack spacing={1.25} mt={1.25}>
          {p.stages.map(st => <StageRow key={st.key} st={st} canConfirm={forVendor} onConfirm={setConfirming} />)}
        </Stack>
      </Collapse>
      {confirming && (
        <ConfirmStageDialog
          booking={booking} stage={confirming} onClose={() => setConfirming(null)}
          onDone={(msg) => { setConfirming(null); onMessage?.(msg); }}
        />
      )}
    </Box>
  );
}
